import Vue from "./../vue";
import VueApollo from "vue-apollo";
import { ApolloClient } from "apollo-client";
import { createHttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws";
import { onError } from "apollo-link-error";
import { setContext } from "apollo-link-context";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloLink, Operation } from "apollo-link";
import { OperationOptions } from "subscriptions-transport-ws/dist/client";

import { settings } from "@xsenscloud/xse-styleguide";
import { AzureB2CService } from "@xsenscloud/vue-authentication";
import router from "@/router";
import store from "@/store";

const debug = process.env.NODE_ENV !== "production";

const httpLink = createHttpLink({
  uri: settings.API_URL + "/graphql"
});

/**
 * Overrides the default URL with the tenant specific backend.
 *
 * @type {ApolloLink}
 */
const tenantLink = new ApolloLink((operation, forward) => {
  const tenant = AzureB2CService.getTenant();
  if (tenant) {
    const uri = (settings.TENANT_API_URL as Record<string, string>)[tenant];
    operation.setContext(() => ({
      uri: uri + "/graphql"
    }));
  }
  return forward(operation);
});

let wsReadyState = false;

export const wsReady = () => {
  return new Promise<void>((resolve, reject) => {
    const maxNumberOfAttempts = 10;
    const intervalTime = 300; //ms

    let currentAttempt = 0;
    const interval = setInterval(() => {
      if (currentAttempt > maxNumberOfAttempts - 1) {
        clearInterval(interval);
        reject(new Error("Maximum number of attempts exceeded"));
      } else if (wsReadyState) {
        clearInterval(interval);
        resolve();
      }
      currentAttempt++;
    }, intervalTime);
  });
};

const MultiPromise = (fn: Function) => async ({ times = 2, timeout = 500 }) =>
  new Promise((resolve, reject) => {
    const looper = (iteration: number) => {
      return iteration >= times
        ? reject()
        : new Promise((_, rej) => fn(resolve, rej)).catch(() => {
            setTimeout(() => {
              looper(iteration + 1);
            }, timeout);
          });
    };
    looper(1);
  });

export const backoffAccessToken = () => {
  return MultiPromise((resolve: any, reject: any) => {
    AzureB2CService.getAccessToken()
      .then(resolve)
      .catch(reject);
  })({ times: 5 });
};

export const wsLink = new WebSocketLink({
  uri:
    (settings.API_URL as string).replace(/^(http(s?):)?\/\//, "ws$2://") +
    "/graphql",
  options: {
    lazy: true,
    reconnect: true,
    connectionParams: async () => {
      const token = await backoffAccessToken();
      return {
        Authorization: `Bearer ${token}`
      };
    },
    connectionCallback: error => {
      console.log("connectionCallback", !error);
      store.commit("global/setWsReady", !error);
      wsReadyState = true;
    }
  },
  ...window.wsServer
});

const subscriptionReadyMiddleware = {
  applyMiddleware(options: OperationOptions, next: Function) {
    wsReady().then(() => next());
  }
};

const subscriptionTokenMiddleware = {
  applyMiddleware: async (options: OperationOptions, next: Function) => {
    const token = await backoffAccessToken();
    options.Authorization = `Bearer ${token}`;
    next();
  }
};

/**
 * Updates the tenant connection to use a tenant specific endpoint.
 * @type {ApolloLink}
 */
const wsTenantLink = new ApolloLink((operation, forward) => {
  const tenant = AzureB2CService.getTenant();
  if (tenant && !window.wsServer) {
    //@ts-ignore
    wsLink.subscriptionClient.url =
      //@ts-ignore
      settings.TENANT_API_URL[tenant].replace(/^(http(s?):)?\/\//, "ws$2://") +
      "/graphql";
  }
  return forward(operation);
});

// add the middleware to the web socket link via the Subscription Transport client
// @ts-ignore
wsLink.subscriptionClient.use([
  subscriptionTokenMiddleware,
  subscriptionReadyMiddleware
]);
// @ts-ignore
wsLink.subscriptionClient.on("connected", () => {
  console.log("connected: wsReadyState", wsReadyState);
});

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await backoffAccessToken();
  // return the headers to the context so httpLink can read them
  const response = {
    headers: {
      ...headers
    }
  };
  if (token) {
    response.headers.authorization = `Bearer ${token}`;
  }
  return response;
});

const errorLink = onError(error => {
  //console.log(error);
  const { networkError, graphQLErrors } = error;
  if (networkError) {
    if (networkError.message === "Failed to fetch") {
      router.push({
        name: "error",
        params: {
          graphQLErrors: graphQLErrors ? graphQLErrors.toString() : "",
          message: "No connection to API could be established."
        }
      });
    } else if (
      networkError.message === "invalid signature" ||
      networkError.message === "jwt expired"
    ) {
      //@ts-ignore
      wsLink.subscriptionClient.close();
      store.dispatch("auth/logout");
      router.push({ name: "login" });
    } else {
      //@ts-ignore
      switch (networkError.statusCode) {
        // Not authorised
        case 403:
        case 401:
          //@ts-ignore
          wsLink.subscriptionClient.close();
          store.dispatch("auth/logout");
          router.push({ name: "login" });
          break;
        default:
          router.push({
            name: "error",
            params: {
              graphQLErrors: graphQLErrors ? graphQLErrors.toString() : "",
              message: networkError.message
            }
          });
          break;
      }
    }
  }
});

const hasSubscriptionOperation = (op: Operation) => {
  const {
    query: { definitions }
  } = op;
  return definitions.some(
    // @ts-ignore
    ({ kind, operation }) =>
      kind === "OperationDefinition" && operation === "subscription"
  );
};

const link = ApolloLink.split(
  hasSubscriptionOperation,
  errorLink.concat(wsTenantLink.concat(wsLink)),
  authLink.concat(errorLink.concat(tenantLink.concat(httpLink)))
);

export const apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache({
    addTypename: true
  }),
  connectToDevTools: debug
});

const apolloProvider = new VueApollo({
  defaultClient: apolloClient
});

// Install the vue plugin
Vue.use(VueApollo);

export default apolloProvider;
